import { attach, createEffect, createEvent, createStore, sample } from 'effector';
import { httpClient } from '@/api';
import { $form, type OnboardForm } from './onboard';
import { navigated, reportSelected, type View } from './router';

export type SearchStatus =
  | 'queued'
  | 'running'
  | 'filtering'
  | 'building'
  | 'completed'
  | 'failed'
  | 'cancelled';

export type SearchSource = { source: string; status: string; findings: number };

export type StartSearchResponse = {
  searchId: string;
  status: SearchStatus;
  etaSeconds: number;
};

export type SearchSnapshot = {
  searchId: string;
  status: SearchStatus;
  progressPct: number;
  etaSeconds: number | null;
  sources: SearchSource[];
};

export type StartSearchPayload = {
  draft: OnboardForm;
  policyAccepted: true;
  preferences: { use_llm_filter: boolean };
};

export const searchReset = createEvent();
export const searchStarted = createEvent<StartSearchResponse>();
export const searchSnapshotReceived = createEvent<SearchSnapshot>();

export const $searchId = createStore<string | null>(null)
  .on(searchStarted, (_, r) => r.searchId)
  .on(searchSnapshotReceived, (_, s) => s.searchId)
  .reset(searchReset);

export const $searchStatus = createStore<SearchStatus | null>(null)
  .on(searchStarted, (_, r) => r.status)
  .on(searchSnapshotReceived, (_, s) => s.status)
  .reset(searchReset);

export const $progressPct = createStore<number>(0)
  .on(searchSnapshotReceived, (_, s) => s.progressPct)
  .reset(searchReset);

export const $sources = createStore<SearchSource[]>([])
  .on(searchSnapshotReceived, (_, s) => s.sources)
  .reset(searchReset);

export const $etaSeconds = createStore<number | null>(null)
  .on(searchSnapshotReceived, (_, s) => s.etaSeconds)
  .reset(searchReset);

// $reportSearchId — ID последнего search'а, чей отчёт сейчас «свежий»:
// обновляется когда snapshot пришёл со статусом completed (или resume на
// completed). НЕ сбрасывается при cancel и старте нового — нужен как
// stable cache-buster для react-query queryKey ['/api/lume/report', id].
//
// Корень: queryKey [..., selectedReportId] с staleTime: Infinity и
// selectedReportId=null (=latest) НЕ менялся между поисками — react-query
// отдавал старый отчёт даже после успешного нового search'а. С searchId
// в key каждый завершённый search обновляет ключ → автоматический refetch.
export const $reportSearchId = createStore<string | null>(null).on(searchSnapshotReceived, (current, s) =>
  s.status === 'completed' ? s.searchId : current,
);

const idemKey = () => {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const startSearchFx = attach({
  source: $form,
  effect: createEffect<OnboardForm, StartSearchResponse>((form) => {
    return httpClient.post<StartSearchResponse>(
      '/api/lume/searches',
      {
        draft: form,
        policyAccepted: true,
        preferences: { use_llm_filter: true },
      } satisfies StartSearchPayload,
      { headers: { 'Idempotency-Key': idemKey() } },
    );
  }),
});

export const fetchCurrentFx = createEffect<void, SearchSnapshot | null>(async () => {
  try {
    return await httpClient.get<SearchSnapshot>('/api/lume/searches/current');
  } catch (err) {
    if ((err as { status?: number })?.status === 404) return null;
    throw err;
  }
});

export const cancelSearchFx = attach({
  source: $searchId,
  effect: createEffect<string | null, void>(async (id) => {
    if (!id) return;
    await httpClient.delete(`/api/lume/searches/${id}`);
  }),
});

const ACTIVE_STATUSES: ReadonlySet<SearchStatus> = new Set(['queued', 'running', 'filtering', 'building']);

/**
 * Восстанавливает контекст: если у пользователя есть незавершённый поиск —
 * перевести на /processing; если последний поиск завершён — на /report;
 * иначе — на /onboard.
 */
export const resumeFx = createEffect<void, SearchSnapshot | null>(() => fetchCurrentFx());

sample({ clock: startSearchFx.doneData, target: searchStarted });
sample({
  clock: fetchCurrentFx.doneData,
  filter: (r): r is SearchSnapshot => r !== null,
  target: searchSnapshotReceived,
});
sample({ clock: cancelSearchFx.done, target: searchReset });

sample({
  clock: resumeFx.doneData,
  filter: (snap): snap is SearchSnapshot => snap !== null && ACTIVE_STATUSES.has(snap.status),
  fn: (): View => 'processing',
  target: navigated,
});
// Resume на completed: сбрасываем выбранный отчёт ДО навигации, чтобы
// Report.tsx запросил latest, а не кэш старого id, открытого ранее в
// ReportHistory. Без этого F5 после нового search'а показывал бы прошлый.
sample({
  clock: resumeFx.doneData,
  filter: (snap): snap is SearchSnapshot => snap !== null && snap.status === 'completed',
  fn: () => null,
  target: reportSelected,
});
sample({
  clock: resumeFx.doneData,
  filter: (snap): snap is SearchSnapshot => snap !== null && snap.status === 'completed',
  fn: (): View => 'report',
  target: navigated,
});
sample({
  clock: resumeFx.doneData,
  filter: (snap): snap is SearchSnapshot =>
    snap !== null && (snap.status === 'cancelled' || snap.status === 'failed'),
  fn: (): View => 'onboard',
  target: navigated,
});
sample({
  clock: resumeFx.doneData,
  filter: (snap) => snap === null,
  fn: (): View => 'onboard',
  target: navigated,
});
// Если /current упал не-401 (500/network) — не оставлять пользователя
// в indefinite view. Уводим на onboard как безопасный fallback. На 401
// сработает httpUnauthorized → loggedOut → App покажет login сам.
sample({
  clock: resumeFx.fail,
  filter: ({ error }) => {
    const status = (error as { status?: number } | null)?.status;
    return status !== 401 && status !== 403;
  },
  fn: (): View => 'onboard',
  target: navigated,
});
